import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  LinearProgress,
  Alert,
  Chip,
} from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import TakeTest, { Question } from '../Tests/TakeTest';
import { learnerService, PlacementMasteryRow } from '../../services/learnerService';
import { generateItemsForSkill } from '../../services/practiceItemGenerationService';

type Step = 'intro' | 'generating' | 'taking' | 'submitting' | 'results' | 'error';

const STATUS_LABEL: Record<string, string> = {
  'not-attempted': 'Not started yet',
  emerging: 'Just getting started',
  developing: 'Making progress',
  secure: 'Looks solid',
};

/**
 * Placement: intro -> generate items per skill -> deliver via TakeTest ->
 * grade -> results (mastery map, no headline percentage). See
 * db_backend/api/placement.js for the deterministic skill-selection and
 * grading side of this flow; this component only orchestrates item
 * generation (client -> ai_backend directly) and delivery (TakeTest).
 */
const SUBJECT_LABEL: Record<string, string> = {
  math: 'math',
  reading: 'reading',
};

const PlacementFlow: React.FC = () => {
  const { learnerId } = useParams<{ learnerId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subject = searchParams.get('subject') || undefined;
  const subjectLabel = subject ? SUBJECT_LABEL[subject] || subject : 'skills';
  const [step, setStep] = useState<Step>('intro');
  const [progressLabel, setProgressLabel] = useState('');
  const [progressPct, setProgressPct] = useState(0);
  const [items, setItems] = useState<Question[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [resultMastery, setResultMastery] = useState<PlacementMasteryRow[]>([]);

  const token = localStorage.getItem('token') || '';

  const handleStart = async () => {
    if (!learnerId) return;
    setStep('generating');
    setError(null);
    try {
      const { placement } = await learnerService.startPlacement(learnerId, token, subject);
      const { skills, itemsPerSkill } = placement;

      const allItems: Question[] = [];
      for (let i = 0; i < skills.length; i++) {
        const skill = skills[i];
        setProgressLabel(`Preparing ${skill.name}...`);
        setProgressPct(Math.round((i / skills.length) * 100));

        // Generate raw items via ai_backend, then validate+cache via
        // db_backend before ever showing them to the child - see
        // practiceService.validateGeneratedItem for the answer-key rules.
        const generated = await generateItemsForSkill(
          { code: skill.code, name: skill.name, gradeLevel: skill.gradeLevel, standards: [] },
          itemsPerSkill
        );
        if (generated.length === 0) continue;

        const { items: validated } = await learnerService.submitPlacementSkillItems(
          learnerId,
          skill.skillId,
          generated,
          token
        );

        validated.forEach((item) => {
          allItems.push({
            _id: item._id,
            question: item.question,
            answers: item.answers,
            correctAnswer: item.correctAnswer,
            explanation: item.explanation,
            standardRefId: item.standardRefId,
            bloomsLevel: item.bloomsLevel,
            answerType: item.answerType,
          });
        });
      }

      if (allItems.length === 0) {
        setError('Unable to prepare placement items right now. Please try again in a moment.');
        setStep('error');
        return;
      }

      setItems(allItems);
      setProgressPct(100);
      setStep('taking');
    } catch (err) {
      console.error('Error starting placement:', err);
      const message = err instanceof Error && err.message ? err.message : 'Something went wrong preparing the placement check. Please try again.';
      setError(message);
      setStep('error');
    }
  };

  const handleSubmitComplete = async (answers: Record<string, string>) => {
    if (!learnerId) return;
    setStep('submitting');
    try {
      const { mastery } = await learnerService.submitPlacement(learnerId, answers, token, subject);
      setResultMastery(mastery);
      setStep('results');
    } catch (err) {
      console.error('Error submitting placement:', err);
      setError('Something went wrong scoring the placement check. Please try again.');
      setStep('error');
    }
  };

  if (step === 'intro') {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            Let's find out where you are
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
            This isn't a test with a score - it's a quick check to see which {subjectLabel}
            {' '}skills already feel solid and which ones to work on next.
          </Typography>
          <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary' }}>
            It takes about 15-20 minutes. Take your time - there's no clock.
          </Typography>
          <Button variant="contained" size="large" onClick={handleStart}>
            Start
          </Button>
        </Paper>
      </Container>
    );
  }

  if (step === 'generating') {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Preparing your placement check...
          </Typography>
          <LinearProgress variant="determinate" value={progressPct} sx={{ mb: 2, height: 8, borderRadius: 4 }} />
          <Typography variant="body2" color="text.secondary">
            {progressLabel}
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (step === 'taking') {
    return (
      <TakeTest
        mode="placement"
        items={items}
        itemsTitle="Placement Check"
        onSubmitComplete={handleSubmitComplete}
      />
    );
  }

  if (step === 'submitting') {
    return (
      <Container maxWidth="sm" sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1">Scoring your results...</Typography>
        </Box>
      </Container>
    );
  }

  if (step === 'error') {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        <Button variant="contained" onClick={handleStart}>
          Try Again
        </Button>
      </Container>
    );
  }

  // Results: no headline percentage - show which skills look solid vs need work.
  const solid = resultMastery.filter((m) => m.status === 'secure' || m.status === 'developing');
  const needsWork = resultMastery.filter((m) => m.status === 'emerging' || m.status === 'not-attempted');

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold', textAlign: 'center' }}>
        Here's what we found
      </Typography>
      <Typography variant="body2" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}>
        This is just a starting point - skills change quickly as your child practices.
      </Typography>

      {solid.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
            Looking solid
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {solid.map((m) => (
              <Chip key={m.skillId} label={m.skillName} color="success" variant="outlined" />
            ))}
          </Box>
        </Paper>
      )}

      {needsWork.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'warning.main' }}>
            Good places to start
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {needsWork.map((m) => (
              <Chip key={m.skillId} label={m.skillName} color="warning" variant="outlined" />
            ))}
          </Box>
        </Paper>
      )}

      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Button variant="contained" size="large" onClick={() => navigate('/dashboard')}>
          See Full Skill Map
        </Button>
      </Box>
    </Container>
  );
};

export default PlacementFlow;
